import type { ReactNode } from 'react'

export type LogicGroupViewGroup<T> = {
  id: string | number
  atoms: T[]
  satisfied?: boolean   // optional flag for gate-style coloring
}

export type LogicGroupViewProps<T> = {
  groups: LogicGroupViewGroup<T>[]
  renderAtom: (atom: T) => ReactNode
  /** Slot rendered at the bottom of each group panel (e.g. "+ Dodaj warunek (I)" button). */
  renderGroupFooter?: (group: LogicGroupViewGroup<T>) => ReactNode
  groupSeparatorLabel?: string
  conditionSeparatorLabel?: string
  emptyMessage?: string
  /** Whether to colour groups by 'satisfied' status (green border for satisfied, red for not). */
  colorBySatisfaction?: boolean
}

/**
 * Read-only counterpart of LogicGroupBuilder. Used for rendering the same OR-of-AND
 * structure outside of an editor (gate side panel, audit views, etc.).
 */
export function LogicGroupView<T>({
  groups,
  renderAtom,
  renderGroupFooter,
  groupSeparatorLabel = 'LUB',
  conditionSeparatorLabel = 'I',
  emptyMessage = 'Brak warunków',
  colorBySatisfaction = false,
}: LogicGroupViewProps<T>) {
  if (groups.length === 0) {
    return <div className="text-xs text-slate-500 italic">{emptyMessage}</div>
  }

  return (
    <div className="flex flex-col gap-2">
      {groups.map((group, gi) => {
        const tone = colorBySatisfaction
          ? group.satisfied
            ? 'border-l-emerald-500 bg-emerald-50/50'
            : 'border-l-red-400 bg-red-50/50'
          : 'border-l-slate-400 bg-slate-50/50'
        return (
          <div key={group.id}>
            {gi > 0 && (
              <div className="flex items-center gap-2 my-1">
                <div className="flex-1 border-t border-slate-300" />
                <span className="rounded bg-amber-100 text-amber-800 border border-amber-300 px-2 py-0.5 text-[10px] font-semibold">
                  {groupSeparatorLabel}
                </span>
                <div className="flex-1 border-t border-slate-300" />
              </div>
            )}

            <div className={`border-l-4 ${tone} rounded-r p-2`}>
              {group.atoms.length === 0 && group.satisfied !== false && (
                <div className="text-xs text-slate-500 italic">{emptyMessage}</div>
              )}
              {group.atoms.length === 0 && group.satisfied === false && (
                <div className="text-xs text-red-700 italic">
                  Brak danych o wymaganiach (sprawdź konfigurację)
                </div>
              )}
              <div className="flex flex-col gap-1">
                {group.atoms.map((atom, ai) => (
                  <div key={ai} className="flex items-start gap-2">
                    {ai > 0 && (
                      <span className="rounded bg-emerald-100 text-emerald-800 border border-emerald-300 px-1 py-0 text-[9px] font-semibold mt-1">
                        {conditionSeparatorLabel}
                      </span>
                    )}
                    <div className="flex-1 text-xs">{renderAtom(atom)}</div>
                  </div>
                ))}
              </div>
              {renderGroupFooter && (
                <div className="mt-2 pt-2 border-t border-slate-200">
                  {renderGroupFooter(group)}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
